import * as React from "react";
import { NotificationExtension } from "../../../../_base/extension/NotificationExtension";
import { saveReturnInward } from "../api/returnInwardApi";
import { showBlockingErrors } from "../components/dialogs";
import { DEFAULT_SERIAL_STATUS } from "../config/returnKinds";
import type { ReturnInwardAction, ReturnInwardState } from "../state/returnInwardReducer";
import type { CreateReturnInwardRequest, ReturnInwardError, ReturnOrderLineView, SubmitStatus } from "../types";
import { validateReturnInward } from "../validation/validateReturnInward";

/**
 * Body gửi lên create-return-in-ward: cùng cấu trúc trang cũ gửi create-in-ward-return,
 * header chỉ gồm các trường TblItemTransactionHeaderCommand dùng tới.
 * `savedId`: id của lần lưu trước khi state chưa kịp cập nhật (bấm lưu liên tiếp).
 */
export const buildSaveRequest = (
  state: ReturnInwardState,
  status: SubmitStatus,
  savedId = 0,
): CreateReturnInwardRequest => {
  const { header, order, rows, transactionId } = state;
  const invId = header.subInvId ?? null;
  // Phiếu đã có: backend chép header gửi lên đè vào phiếu (AutoMapper không bỏ qua CreateDate), nên phải gửi
  // lại ngày tạo, như trang cũ. Ngày giao dịch backend đặt bằng ngày tạo lúc thêm mới
  const createDate = header.createDate || order.createDate || null;
  return {
    sourceId: null,
    tblItemTransactionHeaderCommand: {
      transactionId: transactionId || Number(header.transactionId) || savedId || 0,
      transactionCode: header.transactionCode ?? order.transactionCode ?? "",
      createDate,
      transactionDate: header.transactionDate || createDate,
      sourceCode: header.sourceCode || order.sourceCode || "",
      sourceType: header.sourceType,
      branchId: order.toBranchId ?? null,
      subInvId: invId,
      subSourceId: order.subSourceId ?? null,
      departmentId: header.departmentId ?? null,
      status,
      description: header.description ?? "",
    },
    serials: rows.map((row) => ({
      // Serial chưa chọn tình trạng gửi "Bán bình thường", như trang cũ
      serialStatus: row.serialStatus && String(row.serialStatus).length > 0 ? row.serialStatus : DEFAULT_SERIAL_STATUS,
      attribute11: row.attribute11 && String(row.attribute11).length > 0 ? row.attribute11 : null,
      itemCode: row.itemCode,
      primaryQuantity: row.primaryQuantity,
      subQuantity: row.subQuantity ?? 0,
      serial: row.serial,
      invId,
      note: row.note,
      locatorId: Number(row.locatorId) || null,
      tblInventoryChildSerialOnhandCommands: row.tblInventoryChildSerialOnhandCommands,
    })),
    isLock: status === "LOCK",
  };
};

export interface UseReturnInwardSubmitArgs {
  state: ReturnInwardState;
  dispatch: React.Dispatch<ReturnInwardAction>;
  lines: ReturnOrderLineView[];
  readOnly: boolean;
  /** Bấm "Tới dòng" / "Tới dòng đơn" / "Chọn kho" trong hộp lỗi chặn */
  onGoToIssue?: (error: ReturnInwardError) => void;
}

/**
 * Lưu tạm và hoàn thành phiếu. Nút bấm và phím tắt Ctrl+S / Ctrl+Q cùng gọi `submit`, nên cùng qua một
 * bước kiểm tra. Có cờ chống bấm hai lần; lưu lỗi thì trạng thái phiếu không đổi.
 */
export const useReturnInwardSubmit = ({ state, dispatch, lines, readOnly, onGoToIssue }: UseReturnInwardSubmitArgs) => {
  const busyRef = React.useRef(false);
  /** Id phiếu ngay sau khi lưu thành công, trước khi state render lại */
  const savedIdRef = React.useRef(0);
  const [submitting, setSubmitting] = React.useState<SubmitStatus | null>(null);

  const latest = React.useRef({ state, lines, readOnly, onGoToIssue });
  latest.current = { state, lines, readOnly, onGoToIssue };

  const submit = React.useCallback(
    async (status: SubmitStatus) => {
      if (busyRef.current) return;
      const { state, lines, readOnly, onGoToIssue } = latest.current;

      const errors = validateReturnInward({ status, header: state.header, rows: state.rows, lines, readOnly });
      if (errors.length) {
        showBlockingErrors(status === "LOCK" ? "Chưa thể hoàn thành phiếu" : "Chưa thể lưu phiếu", errors, onGoToIssue);
        return;
      }

      busyRef.current = true;
      setSubmitting(status);
      try {
        const result = await saveReturnInward(buildSaveRequest(state, status, savedIdRef.current));
        if (!result.ok) {
          if (result.responded) {
            NotificationExtension.Fails(status === "LOCK" ? "Khóa phiếu nhập kho thất bại" : "Lưu phiếu nhập kho thất bại");
          }
          return;
        }
        if (result.transactionId) savedIdRef.current = result.transactionId;
        if (status === "OPEN") {
          dispatch({ type: "SAVED", transactionId: result.transactionId });
          NotificationExtension.Success("Lưu phiếu nhập kho thành công");
          return;
        }
        dispatch({ type: "LOCKED", transactionId: result.transactionId, finishDate: new Date().toISOString() });
        NotificationExtension.Success("Khóa phiếu nhập kho thành công");
      } catch (error) {
        console.error("[ReturnInward] lưu phiếu lỗi", error);
        NotificationExtension.Fails("Có lỗi khi lưu phiếu nhập kho, vui lòng thử lại.");
      } finally {
        busyRef.current = false;
        setSubmitting(null);
      }
    },
    [dispatch],
  );

  return { submit, submitting };
};
